<div class="community-editor" *transloco="let t">
  <quill-editor
    #quillRef
    [(ngModel)]="content"
    [modules]="modules"
    [formats]="formats"
    [placeholder]="placeholder || t('community.editor.placeholder')"
    [styles]="{ 'min-height': minHeight + 'px' }"
    (onEditorCreated)="onEditorCreated($event)"
    (onContentChanged)="onContentChange($event)">
  </quill-editor>

  @if (selectedImage()) {
    <div class="community-editor__image-size" role="group" [attr.aria-label]="t('community.editor.image_size.label')">
      <span class="small text-muted me-1">{{ t('community.editor.image_size.label') }}</span>
      @for (percent of imageWidths; track percent) {
        <button type="button" class="btn btn-sm"
                [class.btn-primary]="activeImageWidth() === percent"
                [class.btn-outline-secondary]="activeImageWidth() !== percent"
                (click)="setImageWidth(percent)">
          {{ percent === 100 ? t('community.editor.image_size.original') : percent + '%' }}
        </button>
      }
    </div>
  }

  @if (mentionOpen()) {
    <ul class="community-editor__mentions" role="listbox">
      @if (mentionLoading()) {
        <li class="community-editor__mention community-editor__mention--loading">
          <span class="spinner-border spinner-border-sm" role="status"></span>
          {{ t('community.mentions.loading') }}
        </li>
      } @else if (mentionResults().length === 0) {
        <li class="community-editor__mention community-editor__mention--loading">{{ t('community.mentions.no_results') }}</li>
      } @else {
        @for (u of mentionResults(); track u.id) {
          <li class="community-editor__mention" role="option" (click)="insertMention(u)">
            <strong>{{ u.username }}</strong>
            @if (u.full_name) { <span class="text-muted small ms-1">{{ u.full_name }}</span> }
          </li>
        }
      }
    </ul>
  }

  <div class="community-editor__extras">
    <button
      type="button"
      class="btn btn-sm btn-outline-secondary community-editor__emoji-btn"
      (click)="toggleEmojiPicker()"
      [attr.aria-label]="t('community.editor.toolbar.emoji')">
      😊
    </button>

    <button type="button" class="btn btn-sm btn-outline-secondary ms-1" [disabled]="busy()"
            (click)="insertDivider()" [attr.aria-label]="t('community.editor.toolbar.divider')"
            [title]="t('community.editor.toolbar.divider')">
      ➖
    </button>

    @if (canUploadImage) {
      <button type="button" class="btn btn-sm btn-outline-secondary ms-1" [disabled]="busy()"
              (click)="triggerImagePicker()" [attr.aria-label]="t('community.editor.toolbar.image')"
              [title]="t('community.editor.toolbar.image')">
        🖼️
      </button>
      <input #imageInput type="file" accept="image/*" hidden (change)="onImagePicked($event)" />
    }
    @if (canUploadFile) {
      <button type="button" class="btn btn-sm btn-outline-secondary ms-1" [disabled]="busy()"
              (click)="triggerFilePicker()" [attr.aria-label]="t('community.editor.toolbar.file')"
              [title]="t('community.editor.toolbar.file')">
        📎
      </button>
      <input #fileInput type="file" hidden (change)="onFilePicked($event)" />
    }
    <!-- Always offered: the import stores nothing, so it needs no upload right. -->
    <button type="button" class="btn btn-sm btn-outline-secondary ms-1" [disabled]="busy()"
            (click)="triggerPdfPicker()" [attr.aria-label]="t('community.editor.toolbar.pdf')"
            [title]="t('community.editor.toolbar.pdf')">
      📄
    </button>
    <input #pdfInput type="file" accept="application/pdf,.pdf" hidden (change)="onPdfPicked($event)" />

    @if (canEmbedVideo) {
      <button type="button" class="btn btn-sm btn-outline-secondary ms-1" [disabled]="busy()"
              (click)="toggleVideoPrompt()" [attr.aria-label]="t('community.editor.toolbar.video')"
              [title]="t('community.editor.toolbar.video')">
        🎬
      </button>
    }

    @if (busy()) {
      <span class="spinner-border spinner-border-sm ms-2" role="status"></span>
    }

    <small class="text-muted ms-2">{{ t('community.editor.hint') }}</small>
  </div>

  @if (pdfImported(); as imported) {
    <div class="small text-muted mt-1">
      {{ t('community.editor.pdf_imported', { pages: imported.pages }) }}
      @if (imported.truncated) {
        {{ t('community.editor.pdf_truncated') }}
      }
    </div>
  }

  @if (videoPromptOpen()) {
    <div class="community-editor__video-prompt mt-2 d-flex gap-2">
      <input type="url" class="form-control form-control-sm" [placeholder]="t('community.editor.video_placeholder')"
             [(ngModel)]="videoUrlDraft" (keyup.enter)="submitVideoEmbed()" />
      <button type="button" class="btn btn-sm btn-primary" [disabled]="busy() || !videoUrlDraft.trim()" (click)="submitVideoEmbed()">
        {{ t('community.editor.video_insert') }}
      </button>
      <button type="button" class="btn btn-sm btn-outline-secondary" (click)="toggleVideoPrompt()">
        {{ t('community.threads.cancel') }}
      </button>
    </div>
  }

  @if (emojiPickerOpen()) {
    <div class="community-editor__emoji-picker" role="dialog">
      @for (e of emojis; track e) {
        <button
          type="button"
          class="community-editor__emoji"
          (click)="insertEmoji(e)"
          [attr.aria-label]="e">{{ e }}</button>
      }
    </div>
  }
</div>
